import {
  FINTRAC_STR_DEFINITION_TYPE_CODE_MAP,
  FINTRAC_STR_HOLDER_TYPE_CODE_OPTIONS,
} from 'app/modules/fintracStr/formConstants';
import {
  getDependentPaths,
  getUniqueDefinitionOptions,
} from 'app/modules/fintracStr/formUtils';

import { FintracFormSingleField } from 'app/modules/fintracStr/models';
import { isFalse, isFieldBlank } from 'app/modules/fintracStr/renderConditions';
import {
  U21FormFieldTypes,
  U21SelectOptionProps,
} from 'app/shared/u21-ui/components';
import { isUndefined } from 'lodash';

export const FINTRAC_STR_TRANSACTION_STARTING_ACTIONS_SOURCES_OF_FUNDS_OR_VIRTUAL_CURRENCY_FIELDS: FintracFormSingleField =
  {
    type: 'section',
    repeatable: true,
    label: 'Source of Funds or Virtual Currency',
    name: 'sourcesOfFundsOrVirtualCurrency',
    mustExistInPayload: true,
    mustHaveValueIfExist: false,
    dependentPaths: (sectionIndex, fieldSectionIndex) =>
      getDependentPaths(
        undefined,
        fieldSectionIndex && fieldSectionIndex.length === 1
          ? [
              `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.sourcesOfFundsOrVirtualCurrencyIndicator`,
            ]
          : undefined,
        sectionIndex,
        fieldSectionIndex,
      ),
    hideIfTrue: {
      and: [
        {
          path: (sectionIndex, fieldSectionIndex) =>
            fieldSectionIndex && fieldSectionIndex.length === 1
              ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].details.sourcesOfFundsOrVirtualCurrencyIndicator`
              : '',
          conditions: (value) => isFalse(value),
        },
      ],
    },
    fields: [
      {
        type: U21FormFieldTypes.SELECT,
        name: 'typeCode',
        label: 'Account type',
        fieldProps: (_, mutators, sectionIndex, fieldSectionIndex) =>
          fieldSectionIndex && fieldSectionIndex.length === 2
            ? {
                options: FINTRAC_STR_HOLDER_TYPE_CODE_OPTIONS,
                onChange: () => {
                  mutators.setFieldValueUntyped(
                    `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex[1]}].refId`,
                    '',
                  );
                },
              }
            : { options: FINTRAC_STR_HOLDER_TYPE_CODE_OPTIONS },
        mustExistInPayload: true,
        mustHaveValueIfExist: true,
      },
      {
        type: U21FormFieldTypes.SELECT,
        name: 'refId',
        label: 'Subject name',
        mustExistInPayload: true,
        mustHaveValueIfExist: true,
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            undefined,
            fieldSectionIndex && fieldSectionIndex.length === 2
              ? [
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].sourcesOfFundsOrVirtualCurrency`,
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex[1]}]`,
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex[1]}].typeCode`,
                  'definitions', // Need to listen to definitions since getUniqueDefinitionOptions relies on definitions
                ]
              : ['definitions'],
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          and: [
            {
              path: (sectionIndex, fieldSectionIndex) =>
                fieldSectionIndex && fieldSectionIndex.length === 2
                  ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex[1]}].typeCode`
                  : '',
              conditions: (value) => isFieldBlank(value),
            },
          ],
        },
        fieldProps: (flattenedValues, _, sectionIndex, fieldSectionIndex) => ({
          noOptionsText: ((): string => {
            if (
              !isUndefined(sectionIndex) &&
              fieldSectionIndex &&
              fieldSectionIndex.length === 2
            ) {
              const label =
                FINTRAC_STR_DEFINITION_TYPE_CODE_MAP[
                  flattenedValues[
                    `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex[1]}].typeCode`
                  ]
                ];

              if (label) {
                return `No options available. Please create a new "${label}" definition in the definitions tab.`;
              }
            }

            return `No options available. Please create a new definition in the definitions tab.`;
          })(),
          options: ((): U21SelectOptionProps<string>[] =>
            !isUndefined(sectionIndex) &&
            fieldSectionIndex &&
            fieldSectionIndex.length === 2
              ? getUniqueDefinitionOptions(
                  flattenedValues,
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].sourcesOfFundsOrVirtualCurrency`,
                  `transactions[${sectionIndex}].startingActions[${fieldSectionIndex[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex[1]}]`,
                )
              : [])(),
        }),
      },
      {
        type: 'section',
        repeatable: false,
        label: 'Details',
        name: 'details',
        mustExistInPayload: true,
        mustHaveValueIfExist: true,
        isSectionWrapperHidden: true,
        fields: [
          {
            type: U21FormFieldTypes.TEXT,
            name: 'accountNumber',
            label: 'Account number',
            required: false,
            mustExistInPayload: false,
            mustHaveValueIfExist: false,
            dependentPaths: (sectionIndex, fieldSectionIndex) =>
              getDependentPaths(
                undefined,
                fieldSectionIndex && fieldSectionIndex.length === 2
                  ? [
                      `transactions[${sectionIndex}].startingActions[${fieldSectionIndex?.[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex?.[1]}].details.identifyingNumber`,
                      `transactions[${sectionIndex}].startingActions[${fieldSectionIndex?.[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex?.[1]}].details.policyNumber`,
                    ]
                  : undefined,
                sectionIndex,
                fieldSectionIndex,
              ),
            hideIfTrue: {
              or: [
                {
                  path: (sectionIndex, fieldSectionIndex) =>
                    fieldSectionIndex && fieldSectionIndex.length === 2
                      ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex?.[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex?.[1]}].details.identifyingNumber`
                      : '',
                  conditions: (value) => !isFieldBlank(value),
                },
                {
                  path: (sectionIndex, fieldSectionIndex) =>
                    fieldSectionIndex && fieldSectionIndex.length === 2
                      ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex?.[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex?.[1]}].details.policyNumber`
                      : '',
                  conditions: (value) => !isFieldBlank(value),
                },
              ],
            },
          },
          {
            type: U21FormFieldTypes.TEXT,
            name: 'policyNumber',
            label: 'Policy number',
            required: false,
            mustExistInPayload: false,
            mustHaveValueIfExist: false,
            dependentPaths: (sectionIndex, fieldSectionIndex) =>
              getDependentPaths(
                undefined,
                fieldSectionIndex && fieldSectionIndex.length === 2
                  ? [
                      `transactions[${sectionIndex}].startingActions[${fieldSectionIndex?.[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex?.[1]}].details.identifyingNumber`,
                      `transactions[${sectionIndex}].startingActions[${fieldSectionIndex?.[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex?.[1]}].details.accountNumber`,
                    ]
                  : undefined,
                sectionIndex,
                fieldSectionIndex,
              ),
            hideIfTrue: {
              or: [
                {
                  path: (sectionIndex, fieldSectionIndex) =>
                    fieldSectionIndex && fieldSectionIndex.length === 2
                      ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex?.[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex?.[1]}].details.accountNumber`
                      : '',
                  conditions: (value) => !isFieldBlank(value),
                },
                {
                  path: (sectionIndex, fieldSectionIndex) =>
                    fieldSectionIndex && fieldSectionIndex.length === 2
                      ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex?.[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex?.[1]}].details.identifyingNumber`
                      : '',
                  conditions: (value) => !isFieldBlank(value),
                },
              ],
            },
          },
          {
            type: U21FormFieldTypes.TEXT,
            name: 'identifyingNumber',
            label: 'Identifying number',
            required: false,
            mustExistInPayload: false,
            mustHaveValueIfExist: false,
            dependentPaths: (sectionIndex, fieldSectionIndex) =>
              getDependentPaths(
                undefined,
                fieldSectionIndex && fieldSectionIndex.length === 2
                  ? [
                      `transactions[${sectionIndex}].startingActions[${fieldSectionIndex?.[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex?.[1]}].details.policyNumber`,
                      `transactions[${sectionIndex}].startingActions[${fieldSectionIndex?.[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex?.[1]}].details.accountNumber`,
                    ]
                  : undefined,
                sectionIndex,
                fieldSectionIndex,
              ),
            hideIfTrue: {
              or: [
                {
                  path: (sectionIndex, fieldSectionIndex) =>
                    fieldSectionIndex && fieldSectionIndex.length === 2
                      ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex?.[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex?.[1]}].details.accountNumber`
                      : '',
                  conditions: (value) => !isFieldBlank(value),
                },
                {
                  path: (sectionIndex, fieldSectionIndex) =>
                    fieldSectionIndex && fieldSectionIndex.length === 2
                      ? `transactions[${sectionIndex}].startingActions[${fieldSectionIndex?.[0]}].sourcesOfFundsOrVirtualCurrency[${fieldSectionIndex?.[1]}].details.policyNumber`
                      : '',
                  conditions: (value) => !isFieldBlank(value),
                },
              ],
            },
          },
        ],
      },
    ],
  };
